import { Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import Stack from '@mui/material/Stack'

import { assertType } from 'utils'
import RouteTypes, { RouteType } from 'views/CalculateEmissions/Logistics/RouteTypes'
import Labels from 'views/CalculateEmissions/RFQ/components/Labels'
import ParameterMapping from 'views/CalculateEmissions/RFQ/components/ParameterMapping'
import { resetAutoFill } from 'views/CalculateEmissions/RFQ/utils/operationUtils'
import {
    CountryCode,
    ParameterName,
    PropertyMapping,
    Route,
    RouteMapping,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    ExtraContextLabel,
    LegAutoFillWithUpdate,
    PARAMETER_MAPPING_OUTPUT,
    RFQTemplateState,
} from 'views/CalculateEmissions/RFQ/utils/types'
import {
    getCountryCodeLabel,
    inferAutoFillAndUpdate,
} from 'views/CalculateEmissions/RFQ/utils/utils'

type ValidRoute = Exclude<Route, 'Distance'>

export type RouteErrors = {
    modePicker?: string
    countryCode?: string
    airportCode?: string
    portCode?: string
    latitude?: string
    longitude?: string
    distance?: string
}

export type RouteProps = {
    label: ExtraContextLabel
    state: RFQTemplateState
    routeMapping: RouteMapping | undefined
    onUpdate: (mapping: RouteMapping | undefined, error: RouteErrors | undefined) => void
    autoFill: LegAutoFillWithUpdate['origin']
    errors?: RouteErrors
}

const adaptRouteVal = (val: string): ValidRoute | undefined => {
    switch (val) {
        case 'address':
            return 'Address'
        case 'coordinate':
            return 'Geo coordinates'
        case 'portCode':
            return 'Port code'
        case 'airportCode':
            return 'Airport code'
        default:
            return undefined
    }
}

const adaptRouteValReverse = (val: string): RouteType | undefined => {
    switch (val) {
        case 'Address':
            return RouteType.ADDRESS
        case 'Geo coordinates':
            return RouteType.GEO_COORDINATES
        case 'Port code':
            return RouteType.SEAPORT
        case 'Airport code':
            return RouteType.AIRPORT
        default:
            return undefined
    }
}

// eslint-disable-next-line complexity
const RoutePicker = ({ label, state, routeMapping, onUpdate, autoFill, errors }: RouteProps) => {
    const handleRouteChange = (val: string | undefined) => {
        resetAutoFill(autoFill)

        const route: ValidRoute | undefined = val ? adaptRouteVal(val) : undefined
        const extraContext = {
            keywordExtra: label, // Use 'Origin' or 'Destination' as extra context
            columnLabelBlacklist:
                label === 'Origin' ? ['Destination' as const] : ['Origin' as const],
        }

        function handleAutoFill<T extends ParameterName>(
            parameter: T,
        ): ReturnType<typeof inferAutoFillAndUpdate<T>> {
            return inferAutoFillAndUpdate<T>(state, parameter, autoFill, extraContext)
        }

        const inferenceFields: ParameterName[] = [
            'countryCode',
            'city',
            'streetLine',
            'postcode',
            'airportCode',
            'portCode',
            'lat',
            'lon',
        ]

        const inferenceData = Object.fromEntries(
            inferenceFields.map((e) => [e, handleAutoFill(e)]),
        ) as { [K in (typeof inferenceFields)[number]]: ReturnType<typeof handleAutoFill<K>> }

        if (route === 'Address') {
            const val = {
                type: route,
                countryCode: inferenceData.countryCode,
                city: inferenceData.city,
                streetLine: inferenceData.streetLine,
                postcode: inferenceData.postcode,
            }
            onUpdate(val, undefined)
        } else if (route === 'Airport code') {
            onUpdate({ type: route, airportCode: inferenceData.airportCode }, undefined)
        } else if (route === 'Port code') {
            onUpdate({ type: route, portCode: inferenceData.portCode }, undefined)
        } else if (route === 'Geo coordinates') {
            // Properties are required and of the same type so if one fails the other will too
            onUpdate({ type: route, lat: inferenceData.lat, lon: inferenceData.lon }, undefined)
        } else {
            assertType<undefined>(route)
            onUpdate(undefined, undefined)
        }
    }

    const updateRouteParameters = (
        parameterName: string,
        mapping: PropertyMapping<unknown> | undefined,
    ) => {
        onUpdate(
            {
                ...routeMapping!,
                [parameterName]: mapping,
            } as RouteMapping,
            undefined,
        )
    }

    // TODO Can we improve how `propertyMapping` on each is given? How about syncing here and `handleRouteChange` above?
    const routeParameters: Record<Route, React.ReactNode> = {
        Address: (
            <Box>
                <Labels />
                <ParameterMapping<CountryCode>
                    error={errors?.countryCode}
                    state={state}
                    parameter="countryCode"
                    isCountryCode
                    mappingType="mapping"
                    outputValues={PARAMETER_MAPPING_OUTPUT.countryCode}
                    formatOutput={getCountryCodeLabel}
                    propertyMapping={
                        routeMapping && routeMapping.type === 'Address'
                            ? routeMapping.countryCode
                            : undefined
                    }
                    onUpdate={updateRouteParameters}
                    autoFill={autoFill.Address.countryCode}
                    routeLabel={label}
                    legNumber={state.currentLeg}
                />
                <ParameterMapping<string>
                    state={state}
                    parameter="streetLine"
                    mappingType="direct"
                    outputValues={PARAMETER_MAPPING_OUTPUT.streetLine}
                    formatOutput={String}
                    propertyMapping={
                        routeMapping && routeMapping.type === 'Address'
                            ? routeMapping.streetLine
                            : undefined
                    }
                    onUpdate={updateRouteParameters}
                    autoFill={autoFill.Address.streetLine}
                />
                <ParameterMapping<string>
                    state={state}
                    parameter="city"
                    mappingType="direct"
                    outputValues={PARAMETER_MAPPING_OUTPUT.city}
                    formatOutput={String}
                    propertyMapping={
                        routeMapping && routeMapping.type === 'Address'
                            ? routeMapping.city
                            : undefined
                    }
                    onUpdate={updateRouteParameters}
                    autoFill={autoFill.Address.city}
                />
                <ParameterMapping<string>
                    state={state}
                    parameter="postcode"
                    mappingType="direct"
                    outputValues={PARAMETER_MAPPING_OUTPUT.postcode}
                    formatOutput={String}
                    propertyMapping={
                        routeMapping && routeMapping.type === 'Address'
                            ? routeMapping.postcode
                            : undefined
                    }
                    onUpdate={updateRouteParameters}
                    autoFill={autoFill.Address.postcode}
                />
            </Box>
        ),
        'Airport code': (
            <Box>
                <Labels />
                <ParameterMapping<string>
                    error={errors?.airportCode}
                    state={state}
                    parameter="airportCode"
                    mappingType="direct"
                    outputValues={PARAMETER_MAPPING_OUTPUT.airportCode}
                    formatOutput={String}
                    propertyMapping={
                        routeMapping && routeMapping.type === 'Airport code'
                            ? routeMapping.airportCode
                            : undefined
                    }
                    onUpdate={updateRouteParameters}
                    autoFill={autoFill['Airport code'].airportCode}
                />
            </Box>
        ),
        'Port code': (
            <Box>
                <Labels />
                <ParameterMapping<string>
                    error={errors?.portCode}
                    state={state}
                    parameter="portCode"
                    mappingType="direct"
                    outputValues={PARAMETER_MAPPING_OUTPUT.portCode}
                    formatOutput={String}
                    propertyMapping={
                        routeMapping && routeMapping.type === 'Port code'
                            ? routeMapping.portCode
                            : undefined
                    }
                    onUpdate={updateRouteParameters}
                    autoFill={autoFill['Port code'].portCode}
                />
            </Box>
        ),
        'Geo coordinates': (
            <Box>
                <Labels />
                <ParameterMapping<number>
                    error={errors?.latitude}
                    state={state}
                    parameter="lat"
                    mappingType="direct"
                    outputValues={PARAMETER_MAPPING_OUTPUT.lat}
                    formatOutput={String}
                    propertyMapping={
                        routeMapping && routeMapping.type === 'Geo coordinates'
                            ? routeMapping.lat
                            : undefined
                    }
                    onUpdate={updateRouteParameters}
                    autoFill={autoFill['Geo coordinates'].lat}
                />
                <ParameterMapping<number>
                    error={errors?.longitude}
                    state={state}
                    parameter="lon"
                    mappingType="direct"
                    outputValues={PARAMETER_MAPPING_OUTPUT.lon}
                    formatOutput={String}
                    propertyMapping={
                        routeMapping && routeMapping.type === 'Geo coordinates'
                            ? routeMapping.lon
                            : undefined
                    }
                    onUpdate={updateRouteParameters}
                    autoFill={autoFill['Geo coordinates'].lon}
                />
            </Box>
        ),
        Distance: <></>, // TODO improve types all around so this is not needed
    }

    return (
        <Stack
            direction="column"
            sx={{
                width: '100%',
            }}
            spacing={{
                xs: 4,
            }}
        >
            <Text variant={'h6'} sx={{ mb: 4 }}>
                {label}
            </Text>
            <Box sx={{ width: '100%', maxWidth: '414px' }}>
                <RouteTypes
                    error={errors?.modePicker}
                    customLabel={`Select ${label} input`}
                    showAirportCode={true}
                    showPortCode={true}
                    value={
                        routeMapping?.type
                            ? adaptRouteValReverse(routeMapping.type as string)
                            : undefined
                    }
                    onChange={handleRouteChange}
                />
            </Box>
            {routeMapping !== undefined && routeParameters[routeMapping.type]}
        </Stack>
    )
}

export default RoutePicker
